import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { fly } from '@theme/wc-cart-fx';

/**
 * Progressive enhancement for lightweight "Add to bag" forms used by the redesign sections
 * (hero product card, product card quick add).
 *
 * Markup contract: <wc-add-to-cart><form action="/cart/add" method="post">…<input name="id">…</form></wc-add-to-cart>
 * Without JavaScript the form posts to /cart/add natively. With JavaScript it adds via AJAX and
 * dispatches the standard CartLinesUpdateEvent so the cart drawer, cart bubble and cart items update.
 * The product image (the first img inside the closest [data-wc-fly-scope]) flies into the bag.
 */
class WcAddToCart extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    this.form?.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get form() {
    return this.querySelector('form');
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[type="submit"]'));
  }

  get status() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wc-status]'));
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = this.form;
    if (!form) return;

    event.preventDefault();

    const button = this.button;
    if (button?.getAttribute('aria-disabled') === 'true') return;
    button?.setAttribute('aria-disabled', 'true');
    this.setAttribute('loading', '');

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    // Fly from the clicked control's own scope (e.g. the product page sticky bar thumbnail).
    const submitter = event.submitter instanceof Element ? event.submitter : null;
    const scope = submitter?.closest('[data-wc-fly-scope]') ?? this.closest('[data-wc-fly-scope]');
    const image = scope?.querySelector('[data-wc-fly-image], img');
    fly(image instanceof HTMLImageElement ? image : null);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      const cart = await this.#fetchCart();

      if (data.status) {
        this.#announce(data.message || data.description || '');
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'wc-add-to-cart', itemCount: quantity },
        });
        return;
      }

      this.#announce(this.dataset.addedText || Theme.translations?.added || '');
      this.setAttribute('data-added', '');
      window.setTimeout(() => this.removeAttribute('data-added'), 1800);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'wc-add-to-cart',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      // Fall back to a native submit so the shopper is never stuck.
      form.submit();
    } finally {
      button?.removeAttribute('aria-disabled');
      this.removeAttribute('loading');
    }
  };

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.status;
    if (!status) return;
    status.textContent = message;
    window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('wc-add-to-cart')) customElements.define('wc-add-to-cart', WcAddToCart);
